// src/pages/profile-settings/pient/SettingsLanguage.jsx
import React from "react";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import Radio from "@/components/ui/Radio.jsx";
import Card from "@/components/ui/Card.jsx";
import { LANGUAGES } from "@/config/languages.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const COMING_SOON_LANGUAGE_ORDER = [
  "tn",
  "sn",
  "zu",
  "xh",
  "nd",
  "st",
  "af",
  "bem",
  "ny",
];

const EXTRA_ROADMAP_LANGUAGES = [
  { code: "st", label: "Sesotho" },
];

export default function SettingsLanguage() {
  const { languageCode, appLanguages, market, setLanguage, t } = useLanguage();
  const [snackbar, setSnackbar] = React.useState("");

  const orderedLanguages = React.useMemo(() => {
    const supportedCodes = new Set(
      Array.isArray(market?.supportedLanguages)
        ? market.supportedLanguages
        : []
    );
    const activeSupportedCodes = new Set(
      Array.from(supportedCodes).filter((code) =>
        appLanguages.some((language) => language.code === code),
      ),
    );

    const supported = [];
    const remaining = [];

    for (const language of appLanguages) {
      if (activeSupportedCodes.has(language.code)) {
        supported.push(language);
      } else {
        remaining.push(language);
      }
    }

    return [...supported, ...remaining];
  }, [appLanguages, market]);

  const comingSoonLanguages = React.useMemo(() => {
    const activeCodes = new Set(appLanguages.map((language) => language.code));
    const catalog = [...LANGUAGES, ...EXTRA_ROADMAP_LANGUAGES];
    const byCode = new Map(catalog.map((language) => [language.code, language]));

    return COMING_SOON_LANGUAGE_ORDER
      .filter((code) => !activeCodes.has(code))
      .map((code) => byCode.get(code))
      .filter(Boolean);
  }, [appLanguages]);

  function openSnack(msg) {
    setSnackbar(msg);
    window.clearTimeout(openSnack._t);
    openSnack._t = window.setTimeout(() => setSnackbar(""), 3000);
  }

  function handleSelectLanguage(lang) {
    if (lang.code === languageCode) return;

    setLanguage(lang.code);
    openSnack(
      t("settings.language.changedTo", "Language changed to {language}", {
        language: lang.label,
      })
    );
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        title={t("settings.language.title", "Language")}
        // X just closes back to Settings; SettingsHome already has entry point
        onBack={() => window.history.back()}
      />

      <div className="mt-4 space-y-5">
        <section>
          <div className="mb-3 px-0.5">
            <h2 className="text-title-medium text-on-surface">
              {t("settings.language.availableNow", "Available now")}
            </h2>
            <p className="mt-1 text-body-medium text-on-surface-var">
              {t(
                "settings.language.availableNowCopy",
                "Choose the current AfriMed™ app language for this device and account.",
              )}
            </p>
          </div>

          <Card className="overflow-hidden">
            {orderedLanguages.map((lang, index) => {
              const isActive = languageCode === lang.code;
              return (
                <React.Fragment key={lang.code}>
                  <button
                    type="button"
                    onClick={() => handleSelectLanguage(lang)}
                    className="w-full px-4 py-4 text-left transition active:scale-[0.998]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-title-medium text-on-surface">{lang.label}</div>
                      </div>
                      <Radio
                        checked={isActive}
                        name={lang.label}
                        readOnly
                        variant="afrimed"
                      />
                    </div>
                  </button>

                  {index < orderedLanguages.length - 1 ? (
                    <div
                      className="ml-4 h-px"
                      style={{ background: "var(--Schemes-Outline-Variant,#E6E7EB)" }}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
          </Card>
        </section>

        <section>
          <div className="mb-3 px-0.5">
            <h2 className="text-title-medium text-on-surface">
              {t("settings.language.comingSoonTitle", "Coming soon")}
            </h2>
            <p className="mt-1 text-body-medium text-on-surface-var">
              {t(
                "settings.language.comingSoonCopy",
                "Southern African languages are being prepared carefully for healthcare-safe rollout.",
              )}
            </p>
          </div>

          <Card className="overflow-hidden">
            {comingSoonLanguages.map((lang, index) => (
              <React.Fragment key={lang.code}>
                <div
                  className="px-4 py-4 opacity-65"
                  aria-disabled="true"
                >
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-title-medium text-on-surface">{lang.label}</div>
                      <p className="mt-1 text-body-small text-on-surface-var">
                        {t("common.comingSoon", "Coming soon")}
                      </p>
                    </div>
                    <Radio
                      checked={false}
                      name={lang.label}
                      readOnly
                      variant="afrimed"
                    />
                  </div>
                </div>

                {index < comingSoonLanguages.length - 1 ? (
                  <div
                    className="ml-4 h-px"
                    style={{ background: "var(--Schemes-Outline-Variant,#E6E7EB)" }}
                  />
                ) : null}
              </React.Fragment>
            ))}
          </Card>
        </section>
      </div>

      <Snackbar open={!!snackbar} onClose={() => setSnackbar("")}>
        {snackbar}
      </Snackbar>
    </SettingsScreenShell>
  );
}
